import { useMutation, useQueryClient } from "@tanstack/react-query";

import Sentry from "@similarweb/sentry-logger";

import { useToasts } from "app/pages/activation/hooks/helpers/useToasts";
import { translate } from "app/filters/i18nFilter";
import type { TopicSchema, TopicWithKeywordsSchema } from "app/pages/activation/components/Topics/types";
import { ACTIVATION_OBJECT_QUERY_KEY } from "app/pages/activation/queries/data/useActivationObjectQuery";
import { TOPICS_QUERY_KEY } from "app/pages/activation/queries/topics/useGetTopicsDataQuery";
import { createActivationApi } from "app/pages/activation/services/api";

import type { ActivationObjectSchema } from "../../types/api";
import type { BenchmarkGranularity } from "../../types/common";

const CREATE_TOPICS_MUTATION_KEY = "CREATE_TOPICS";
type CreateTopicsMutationSchema = {
    activationId: string;
    body: string[] | TopicWithKeywordsSchema[];
    isWithKeywords?: boolean;
    isFromExistingKW?: boolean;
};

export type CreatedTopicDTO = { id: string; name: string };
type OptimisticTopic = TopicSchema & { __optimistic?: boolean };
type CreateTopicsWithCb = CreateTopicsMutationSchema & { __onSuccess?: (data: CreatedTopicDTO[]) => void };

const makeOptimisticId = (name: string) => `optimistic-${name}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
const makePreloaded = (name: string, id?: string, optimistic?: boolean): OptimisticTopic => ({
    topicName: name,
    topicId: id ?? makeOptimisticId(name),
    isPreloading: true,
    created: Date.now().toString(),
    keywordsCount: 0,
    leader: { domain: "", shareOfVoice: 0, shareOfVoiceChange: 0 },
    primary: { domain: "", shareOfVoice: 0, shareOfVoiceChange: 0 },
    others: { share: 0, competitors: [] },
    volume: 0,
    isShared: false,
    volumeChange: 0,
    trend: [],
    ...(optimistic ? { __optimistic: true } : {}),
});
const toPreloadedTopic = (topic: CreatedTopicDTO): TopicSchema => ({
    topicId: topic.id,
    topicName: topic.name,
    isPreloading: true,
    created: Date.now().toString(),
    keywordsCount: 0,
    leader: { domain: "", shareOfVoice: 0, shareOfVoiceChange: 0 },
    primary: { domain: "", shareOfVoice: 0, shareOfVoiceChange: 0 },
    others: { share: 0, competitors: [] },
    volume: 0,
    isShared: false,
    volumeChange: 0,
    trend: [],
});
export const useCreateTopicsMutation = (activationId: string, granularity: BenchmarkGranularity) => {
    const queryClient = useQueryClient();
    const { showErrorToast } = useToasts();
    const topicsKey = [TOPICS_QUERY_KEY, granularity, activationId] as const;
    const activationKey = [ACTIVATION_OBJECT_QUERY_KEY, activationId] as const;

    return useMutation<
        CreatedTopicDTO[],
        Error,
        CreateTopicsWithCb,
        { prevTopics?: TopicSchema[]; prevActivation?: ActivationObjectSchema | undefined; optimisticIds?: string[] }
    >({
        mutationKey: [CREATE_TOPICS_MUTATION_KEY],
        mutationFn: async ({ activationId, body, isWithKeywords = false, isFromExistingKW = false }) => {
            const api = createActivationApi();
            if (isFromExistingKW) {
                return api.topics.createFromExistingKW(activationId, body as string[]);
            }
            if (isWithKeywords) {
                return api.topics.createWithKW(activationId, body as TopicWithKeywordsSchema[]);
            }
            return api.topics.create(activationId, body as string[]);
        },
        onMutate: async (variables) => {
            const { body } = variables;
            const names: string[] =
                Array.isArray(body) && typeof (body as any)[0] === "string"
                    ? (body as string[])
                    : Array.isArray(body) && typeof (body as any)[0] === "object"
                      ? (body as TopicWithKeywordsSchema[]).map((t) => t.name)
                      : [];
            if (!names.length) {
                throw new Error("No topics to create");
            }

            await queryClient.cancelQueries({ queryKey: topicsKey });
            await queryClient.cancelQueries({ queryKey: activationKey });
            const prevTopics: OptimisticTopic[] = queryClient.getQueryData(topicsKey) ?? [];
            const prevActivation: ActivationObjectSchema = queryClient.getQueryData(activationKey);

            const optimistic = names.map((n) => makePreloaded(n, undefined, true));

            queryClient.setQueryData<TopicSchema[]>(topicsKey, [...optimistic, ...prevTopics]);

            const currentActivationTopics = Array.isArray(prevActivation?.assets?.topics)
                ? prevActivation.assets.topics
                : [];

            const optimisticIds = optimistic.map(({ topicId }) => topicId);

            queryClient.setQueryData(activationKey, {
                ...(prevActivation ?? { id: activationId, assets: { topics: [] } }),
                assets: { ...(prevActivation?.assets ?? {}), topics: [...currentActivationTopics, ...optimisticIds] },
            });
            return { prevTopics, prevActivation, optimisticIds };
        },
        onSuccess: (data, variables, ctx) => {
            try {
                const existing = queryClient.getQueryData(topicsKey) ?? [];
                const withoutOptimistic = (existing as OptimisticTopic[]).filter(
                    (t) => !t.__optimistic,
                ) as TopicSchema[];
                const real = (data ?? []).map(toPreloadedTopic);
                queryClient.setQueryData<TopicSchema[]>(topicsKey, [...real, ...withoutOptimistic]);

                const activation: ActivationObjectSchema = queryClient.getQueryData(activationKey);
                const prevTopicIds = Array.isArray(activation.assets?.topics) ? activation.assets.topics : [];
                const cleaned = prevTopicIds.filter((id) => !ctx?.optimisticIds?.includes(id));
                const realIds = real.map((r) => r.topicId);

                queryClient.setQueryData(activationKey, {
                    ...activation,
                    assets: { ...(activation.assets ?? {}), topics: [...cleaned, ...realIds] },
                });
            } catch {}
            try {
                variables.__onSuccess?.(data);
            } catch {}
        },
        onError: (error, _vars, ctx) => {
            if (ctx?.prevTopics) {
                queryClient.setQueryData(topicsKey, ctx.prevTopics);
            }
            if (ctx?.prevActivation) {
                queryClient.setQueryData(activationKey, ctx.prevActivation);
            }
            Sentry.errorTracker({ e: error, level: "error", message: "Error mutating topics" });
            showErrorToast(translate("common.api.error", { error: error?.message }));
        },
        onSettled: async () => {
            await queryClient.invalidateQueries({ queryKey: topicsKey });
            await queryClient.invalidateQueries({ queryKey: activationKey });
        },
    });
};
